// API Service with mock data support for demo mode
import { isDemoMode, API_BASE_URL } from '../config';
import axios from 'axios';
import axiosInstance from '../utils/axiosConfig';
import {
  mockSubmissions,
  mockResultsData,
  mockAnalytics,
  mockCompanies,
  mockDomainEmails,
  mockPortfolioChart
} from '../mockData';

// Simulate API delay for realistic demo experience
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Helper to generate random submission ID for demo
const generateDemoSubmissionId = () => {
  return 'demo-sub-' + Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
};

export const apiService = {
  // Get results for a specific submission
  async getResults(submissionId) {
    if (isDemoMode) {
      console.log('📊 [DEMO] Fetching results for:', submissionId);
      await delay(500);
      
      // Try to find the submission in mock data
      const submission = mockSubmissions.find(s => s.submission_id === submissionId);
      
      if (submission) {
        return {
          ...submission,
          chart_data: {
            tech_feasibility_score: submission.tech_feasibility_score,
            roi_potential_score: submission.roi_potential_score,
            strategic_value_score: submission.strategic_value_score
          }
        };
      }
      
      // If not found, return default demo data
      return mockResultsData;
    }
    
    // Real API call
    const response = await fetch(`${API_BASE_URL}/api/results/${submissionId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch results');
    }
    return response.json();
  },

  // Get company portfolio
  async getCompanyPortfolio(domainName) {
    if (isDemoMode) {
      console.log('🏢 [DEMO] Fetching company portfolio for:', domainName);
      await delay(600);
      
      // Filter submissions by domain
      const companySubmissions = mockSubmissions.filter(s => s.domain_name === domainName);
      
      return {
        domain_name: domainName,
        company_name: domainName.split('.')[0].charAt(0).toUpperCase() + domainName.split('.')[0].slice(1),
        total_submissions: companySubmissions.length,
        submissions: companySubmissions
      };
    }
    
    // Real API call
    const response = await fetch(`${API_BASE_URL}/api/company-portfolio/${domainName}`);
    if (!response.ok) {
      throw new Error('Failed to fetch company portfolio');
    }
    return response.json();
  },

  // Submit survey
  async submitSurvey(data) {
    if (isDemoMode) {
      console.log('📝 [DEMO] Submitting survey:', data);
      await delay(1000);
      
      const newSubmissionId = generateDemoSubmissionId();
      
      return {
        submission_id: newSubmissionId,
        message: 'Demo submission successful! Results have been calculated.',
        tech_feasibility_score: Math.floor(Math.random() * 40) + 60,
        roi_potential_score: Math.floor(Math.random() * 40) + 60,
        strategic_value_score: Math.floor(Math.random() * 40) + 60,
        strategic_category: ['Quick Wins', 'Strategic Challenges', 'Technical Experiments'][Math.floor(Math.random() * 3)]
      };
    }
    
    // Real API call
    const response = await axios.post(`${API_BASE_URL}/api/submit`, data);
    return response.data;
  },

  // Admin login
  async adminLogin(credentials) {
    if (isDemoMode) {
      console.log('🔐 [DEMO] Admin login attempt:', credentials.username);
      await delay(700);
      
      // In demo mode, accept any credentials
      return {
        access_token: 'demo-access-token-' + Date.now(),
        refresh_token: 'demo-refresh-token-' + Date.now(),
        token_type: 'bearer',
        expires_in: 1800
      };
    }
    
    // Real API call
    const response = await axios.post(`${API_BASE_URL}/api/admin/login`, credentials);
    return response.data;
  },

  // Get all submissions (admin)
  async getSubmissions() {
    if (isDemoMode) {
      console.log('📋 [DEMO] Fetching all submissions');
      await delay(600);
      return mockSubmissions;
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get('/api/admin/submissions');
    return response.data.submissions || response.data;
  },

  // Get analytics (admin)
  async getAnalytics() {
    if (isDemoMode) {
      console.log('📈 [DEMO] Fetching analytics');
      await delay(500);
      return mockAnalytics;
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get('/api/admin/analytics');
    return response.data;
  },

  // Get companies list (admin)
  async getCompanies() {
    if (isDemoMode) {
      console.log('🏢 [DEMO] Fetching companies list');
      await delay(400);
      return mockCompanies;
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get('/api/admin/companies');
    return response.data.companies || response.data;
  },

  // Get emails for a domain (admin)
  async getDomainEmails(domainName) {
    if (isDemoMode) {
      console.log('📧 [DEMO] Fetching emails for domain:', domainName);
      await delay(400);
      return mockDomainEmails[domainName] || [];
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get(`/api/admin/domain-emails/${domainName}`);
    return response.data;
  },

  // Delete submission (admin)
  async deleteSubmission(submissionId) {
    if (isDemoMode) {
      console.log('🗑️ [DEMO] Deleting submission:', submissionId);
      await delay(500);
      return { message: 'Demo: Submission deleted successfully' };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.delete(`/api/admin/submissions/${submissionId}`);
    return response.data;
  },

  // Resend email (admin)
  async resendEmail(submissionId) {
    if (isDemoMode) {
      console.log('📨 [DEMO] Resending email for submission:', submissionId);
      await delay(800);
      return { message: 'Demo: Email sent successfully' };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.post(`/api/admin/resend-email/${submissionId}`);
    return response.data;
  },

  // Send survey invitation (admin)
  async sendSurveyInvitation(data) {
    if (isDemoMode) {
      console.log('📧 [DEMO] Sending survey invitation:', data);
      await delay(1000);
      return { message: 'Demo: Survey invitation sent successfully' };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.post('/api/admin/send-survey-invitation', data);
    return response.data;
  },

  // Send survey results (admin)
  async sendSurveyResults(data) {
    if (isDemoMode) {
      console.log('📊 [DEMO] Sending survey results:', data);
      await delay(1000);
      return { message: 'Demo: Survey results sent successfully' };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.post('/api/admin/send-survey-results', data);
    return response.data;
  },

  // Bulk upload submissions (admin)
  async bulkUpload(file) {
    if (isDemoMode) {
      console.log('📤 [DEMO] Bulk upload:', file.name);
      await delay(1500);
      return {
        message: 'Demo: Bulk upload processed successfully',
        successful: 5,
        failed: 0
      };
    }
    
    // Real API call with authentication
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post('/api/admin/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Store chart image
  async storeChartImage(data) {
    if (isDemoMode) {
      console.log('🖼️ [DEMO] Storing chart image');
      await delay(500);
      return { message: 'Demo: Chart image stored successfully' };
    }
    
    // Real API call
    const response = await axios.post(`${API_BASE_URL}/api/store-chart-image`, data);
    return response.data;
  },

  // Get portfolio chart data (admin)
  async getPortfolioChart() {
    if (isDemoMode) {
      console.log('📊 [DEMO] Fetching portfolio chart');
      await delay(500);
      return mockPortfolioChart;
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get('/api/admin/portfolio-chart');
    return response.data;
  },

  // Get company chart data (admin)
  async getCompanyChart(domainName) {
    if (isDemoMode) {
      console.log('🏢 [DEMO] Fetching company chart for:', domainName);
      await delay(500);
      
      // Filter portfolio data for specific company
      const companySubmissions = mockSubmissions.filter(s => s.domain_name === domainName);
      const filteredChart = {
        ...mockPortfolioChart,
        categorized_data: {}
      };
      
      // Filter chart data to only include submissions from this company
      Object.keys(mockPortfolioChart.categorized_data).forEach(category => {
        filteredChart.categorized_data[category] = mockPortfolioChart.categorized_data[category].filter(
          item => companySubmissions.some(sub => sub.submission_id === item.submission_id)
        );
      });
      
      return filteredChart;
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get(`/api/admin/company-chart/${domainName}`);
    return response.data;
  },

  // Get public chart data (no auth required)
  async getPublicChartData(submissionId) {
    if (isDemoMode) {
      console.log('📊 [DEMO] Fetching public chart for submission:', submissionId);
      await delay(500);
      
      // Find the submission and return filtered chart data
      const submission = mockSubmissions.find(s => String(s.submission_id) === String(submissionId));
      console.log('📊 Found submission:', submission);
      
      if (!submission) {
        console.warn('📊 Submission not found, returning full portfolio');
        return mockPortfolioChart;
      }
      
      const filteredChart = {
        ...mockPortfolioChart,
        categorized_data: {}
      };
      
      // Only include the requested submission
      Object.keys(mockPortfolioChart.categorized_data).forEach(category => {
        const filtered = mockPortfolioChart.categorized_data[category].filter(
          item => String(item.submission_id) === String(submissionId)
        );
        console.log(`📊 Category ${category}: found ${filtered.length} items`);
        filteredChart.categorized_data[category] = filtered.map(item => ({ 
          ...item, 
          highlighted: true 
        }));
      });
      
      console.log('📊 Filtered chart data:', filteredChart);
      return filteredChart;
    }
    
    // Real API call
    const response = await axios.get(`${API_BASE_URL}/api/chart-data/${submissionId}`);
    return response.data;
  },

  // Get admin chart data (auth required)
  async getAdminChartData(submissionId) {
    if (isDemoMode) {
      return this.getPublicChartData(submissionId);
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get(`/api/admin/chart-data/${submissionId}`);
    return response.data;
  },

  // Get company submissions (admin)
  async getCompanySubmissions(domainName) {
    if (isDemoMode) {
      console.log('🏢 [DEMO] Fetching company submissions for:', domainName);
      await delay(400);
      
      const companySubmissions = mockSubmissions.filter(s => s.domain_name === domainName);
      return { submissions: companySubmissions };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.get(`/api/admin/company-submissions/${domainName}`);
    return response.data;
  },

  // Send submission email (admin)
  async sendSubmissionEmail(data) {
    if (isDemoMode) {
      console.log('📧 [DEMO] Sending submission email:', data);
      await delay(1000);
      return { 
        message: 'Demo: Submission email sent successfully',
        successful_sends: data.to_emails.length,
        failed_sends: 0
      };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.post('/api/admin/send-submission-email', data, { timeout: 30000 });
    return response.data;
  },

  // Send company emails (admin)
  async sendCompanyEmails(domainName, selectedEmails) {
    if (isDemoMode) {
      console.log('📧 [DEMO] Sending company emails:', domainName, selectedEmails);
      await delay(1500);
      return {
        message: 'Demo: Company emails sent successfully',
        successful_sends: selectedEmails.map((email, i) => ({
          email,
          submission_id: `demo-sub-00${i + 1}`
        })),
        failed_sends: [],
        total_attempted: selectedEmails.length,
        successful_count: selectedEmails.length,
        failed_count: 0
      };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.post(
      `/api/admin/send-company-emails/${domainName}`,
      { selectedEmails },
      { timeout: 30000 }
    );
    return response.data;
  },

  // Send survey results email (admin)
  async sendSurveyResultsEmail(data) {
    if (isDemoMode) {
      console.log('📊 [DEMO] Sending survey results email:', data);
      await delay(1200);
      return {
        message: 'Demo: Survey results email sent successfully',
        successful_sends: data.to_emails.length,
        failed_sends: 0
      };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.post(
      '/api/admin/send-survey-results-email',
      data,
      { timeout: 30000 }
    );
    return response.data;
  },

  // Bulk upload surveys (admin)
  async bulkUploadSurveys(data) {
    if (isDemoMode) {
      console.log('📤 [DEMO] Bulk uploading surveys:', data);
      await delay(1500);
      return {
        message: 'Demo: Surveys uploaded successfully',
        successful_count: data.submissions?.length || 0,
        failed_count: 0,
        failed_submissions: []
      };
    }
    
    // Real API call with authentication
    const response = await axiosInstance.post('/api/bulk-upload-surveys', data);
    return response.data;
  }
};

export default apiService;
